import React, { FC, useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  useEffectLoader,
  useLoader,
  useResourceApi,
} from "ackbox-weblib-react";
import { RESOURCES_API_PATH } from "./Constants";
import { faCalendar, faTasks } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const PATH = "notification-settings";

export const Settings: FC = () => {
  const [emails, setEmails] = useState("");
  const [maxWindowMonths, setMaxWindowMonths] = useState(3);
  const [userId, setUserId] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const resourceApi = useResourceApi(RESOURCES_API_PATH);
  const loader = useLoader();

  const loadSettings = async () => {
    const resources = await resourceApi.getValues(PATH);
    if (resources && resources.length > 0) {
      const settings = resources[0];
      setSettingsId(settings.id);
      setCreatedAt(settings.createdAt);
      setUserId(settings.userId);
      setEmails(settings.emailsCsv || "");
      setMaxWindowMonths(settings.maxWindowMonths || 3);
    }
  };

  useEffectLoader(loader, loadSettings, []);

  const validateEmails = (csv: string): boolean => {
    if (!csv) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const items = csv.split(",").map((s) => s.trim());
    for (const item of items) {
      if (item && !emailRegex.test(item)) {
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    setValidationErrors([]);
    if (!validateEmails(emails)) {
      setValidationErrors(["Invalid email format found in the list."]);
      return;
    }
    loader.load(async () => {
      const payload = {
        id: settingsId,
        emailsCsv: emails,
        maxWindowMonths: maxWindowMonths,
        createdAt: createdAt || new Date().toISOString(),
        userId: userId,
      };
      if (settingsId) {
        await resourceApi.patchValue(PATH, payload, settingsId);
      } else {
        await resourceApi.postValue(PATH, payload);
        await loadSettings(); // Reload to get the ID
      }
    });
  };

  return (
    <div className="section">
      <h1 className="title is-spaced">Settings</h1>
      <div className="box">
        <h2 className="subtitle is-5">Notification Settings</h2>
        <AckErrorMessage errors={[...loader.errors, ...validationErrors]} />
        <AckLoader isLoading={loader.isLoading} />
        {!loader.isLoading && (
          <>
            <div className="field">
              <label className="label" htmlFor="emails">
                Emails
              </label>
              <div className="field has-addons">
                <div className="control">
                  <a className="button is-static">
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faTasks} />
                    </span>
                  </a>
                </div>
                <div className="control is-expanded">
                  <input
                    id="emails"
                    className="input"
                    type="text"
                    placeholder="email1@example.com, email2@example.com"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                  />
                </div>
              </div>
              <p className="help">Comma-separated list of email addresses.</p>
            </div>
            <div className="field">
              <label className="label" htmlFor="maxWindowMonths">
                Max Window (Months)
              </label>
              <div className="field has-addons">
                <div className="control">
                  <a className="button is-static">
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faCalendar} />
                    </span>
                  </a>
                </div>
                <div className="control is-expanded">
                  <input
                    id="maxWindowMonths"
                    className="input"
                    type="number"
                    value={maxWindowMonths}
                    onChange={(e) =>
                      setMaxWindowMonths(parseInt(e.target.value))
                    }
                  />
                </div>
              </div>
              <p className="help">
                Number of months to look back for aggregation.
              </p>
            </div>
            <div className="field">
              <div className="control">
                <button className="button is-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
